import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Store, Keyboard, Home as HomeIcon, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Search } from "lucide-react";
import { fetchGames } from "@/lib/api";
import { getGameMemories, addGameMemory, getObjectMemories, addObjectMemory } from "@/lib/memories";
import { useKeyboardMovement } from "@/hooks/useKeyboardMovement";
import { AREAS, layoutPositions } from "@/data/mohallaAreas";
import { Avatar } from "@/components/game/Avatar";
import { MohallaZone } from "@/components/game/MohallaZone";
import { MohallaDecor } from "@/components/game/MohallaDecor";
import { ObjectFind } from "@/components/game/ObjectFind";
import { FindMemoryCard } from "@/components/game/FindMemoryCard";
import { OBJECT_FINDS } from "@/data/mohallaFinds";
import { startGali, stopGali } from "@/lib/galiAmbience";
import { GameCard } from "@/components/GameCard";
import { PLAYABLE_BY_NAME } from "@/components/games/registry";
import { GamesCollectionPanel } from "@/components/GamesCollectionPanel";
import { AmbientMusic } from "@/components/AmbientMusic";
import { MoveHint } from "@/components/game/MoveHint";
import { useIsTouch } from "@/hooks/useIsTouch";
import { GameArcade } from "@/components/games/GameArcade";

const BOUNDS = { minX: 5, maxX: 95, minY: 16, maxY: 88 };
const THRESHOLD = 9;
const START = { x: 50, y: 82 };

export default function MohallaGames() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areaIdx, setAreaIdx] = useState(0);
  const [memories, setMemories] = useState(getGameMemories());
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState({ open: false, tab: "discover" });
  const [arcadeKey, setArcadeKey] = useState(null);
  const [objMem, setObjMem] = useState(getObjectMemories());
  const [find, setFind] = useState(null);

  const anyOverlay = !!selected || panel.open || !!arcadeKey || !!find;
  const isTouch = useIsTouch();
  const { pos, setPos, facing, moving, moveTo } = useKeyboardMovement({ initial: START, bounds: BOUNDS, enabled: !anyOverlay });

  const tapToWalk = (e) => {
    if (anyOverlay) return;
    const r = e.currentTarget.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  useEffect(() => {
    fetchGames().then(setGames).catch(() => toast.error("Colony failed to load")).finally(() => setLoading(false));
  }, []);

  const area = AREAS[areaIdx];

  const zones = useMemo(() => {
    const byName = (a, b) => a.name.localeCompare(b.name);
    const playable = games.filter((g) => PLAYABLE_BY_NAME[g.name]).sort(byName);
    const rest = games.filter((g) => !PLAYABLE_BY_NAME[g.name]).sort(byName);
    const ordered = [...playable, ...rest];
    const inArea = ordered.slice(area.from, area.to);
    const positions = layoutPositions(inArea.length);
    const gameZones = inArea.map((g, i) => ({ kind: "game", key: g.id, ...g, ...positions[i] }));
    const paths = [];
    if (areaIdx > 0) paths.push({ kind: "path", key: "path-prev", dir: "prev", label: AREAS[areaIdx - 1].name, x: 5, y: 46 });
    if (areaIdx < AREAS.length - 1) paths.push({ kind: "path", key: "path-next", dir: "next", label: AREAS[areaIdx + 1].name, x: 95, y: 46 });
    const home = { kind: "home", key: "home", x: 12, y: 89 };
    // nav tiles first, game tiles last -> games render on top and always win taps on any overlap
    return [home, ...paths, ...gameZones];
  }, [games, area.id, areaIdx]);

  const activeZone = useMemo(() => {
    let best = null;
    let bestD = THRESHOLD;
    for (const z of zones) {
      const d = Math.hypot(z.x - pos.x, z.y - pos.y);
      if (d < bestD) { bestD = d; best = z; }
    }
    return best;
  }, [zones, pos]);

  const goArea = (idx) => {
    setAreaIdx(idx);
    setPos({ x: idx > areaIdx ? 12 : idx < areaIdx ? 88 : 50, y: 82 });
  };

  const interact = (zone) => {
    if (!zone) return;
    if (zone.kind === "home") {
      navigate("/");
      return;
    }
    if (zone.kind === "path") {
      goArea(zone.dir === "next" ? areaIdx + 1 : areaIdx - 1);
      return;
    }
    setSelected(zone);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Enter" && !anyOverlay && activeZone) {
        e.preventDefault();
        interact(activeZone);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyOverlay, activeZone]);

  const handleAdd = (id) => {
    setMemories(addGameMemory(id));
    toast.success("+1 Memory ❤️  Yaad aa gayi!");
  };

  const handleAddFind = (key) => {
    setObjMem(addObjectMemory(key));
    toast.success("+1 Yaad ❤️  Sambhaal li!");
  };

  // evening gali soundscape (kids shouting, cycle bells, hawkers, temple bell)
  useEffect(() => { startGali("evening"); return () => stopGali(); }, []);

  // touch dpad
  const holdRef = useRef(null);
  const nudge = (dx, dy) =>
    setPos((p) => ({
      x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, p.x + dx)),
      y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, p.y + dy)),
    }));
  const startHold = (dx, dy) => {
    if (anyOverlay) return;
    nudge(dx, dy);
    holdRef.current = setInterval(() => nudge(dx, dy), 40);
  };
  const stopHold = () => holdRef.current && clearInterval(holdRef.current);

  return (
    <div className="min-h-screen bg-background paper-grain">
      <AmbientMusic src="/audio/games_bg.mp3" />
      {/* HUD */}
      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-kirana-green text-kirana-cream">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5">
          <div>
            <h1 className="font-signboard text-2xl font-bold leading-none sm:text-3xl">Mohalla Games · मोहल्ला</h1>
            <p className="text-[11px] text-kirana-cream/80">Outdoor Indian Childhood Games · 1995–2002</p>
          </div>
          <div className="flex items-center gap-2">
            <div data-testid="games-hud-count" className="hidden items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/20 px-3 py-1.5 text-sm font-bold sm:flex">
              <Heart className="h-4 w-4 fill-kirana-cream" /> {memories.length} / {games.length || 21}
            </div>
            <div data-testid="finds-hud-count" className="hidden items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/20 px-3 py-1.5 text-sm font-bold sm:flex" title="Purani cheezein (yaadein)">
              <Search className="h-4 w-4" /> {objMem.length} / {OBJECT_FINDS.length}
            </div>
            <button
              data-testid="open-my-games"
              onClick={() => setPanel({ open: true, tab: "mine" })}
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-brown bg-kirana-gold px-3 py-1.5 text-sm font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm"
            >
              <Heart className="h-4 w-4" /> <span className="hidden sm:inline">My Games</span>
            </button>
            <Link
              data-testid="open-home"
              to="/"
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-black/20"
            >
              <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">Home</span>
            </Link>
            <Link
              data-testid="open-kirana"
              to="/kirana"
              className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold transition-colors hover:bg-black/20"
            >
              <Store className="h-4 w-4" /> <span className="hidden sm:inline">Kirana Store</span>
            </Link>
          </div>
        </div>
        {/* area tabs */}
        <div className="mx-auto flex max-w-6xl gap-1.5 overflow-x-auto scrollbar-thin px-4 pb-2">
          {AREAS.map((a, i) => (
            <button
              key={a.id}
              data-testid={`area-tab-${a.id}`}
              onClick={() => goArea(i)}
              className={`whitespace-nowrap rounded-full border-2 border-kirana-brown px-3 py-1 text-xs font-bold transition-colors ${i === areaIdx ? "bg-kirana-gold text-kirana-brown" : "bg-black/15 text-kirana-cream hover:bg-black/30"}`}
            >
              {a.name}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-signboard text-lg text-kirana-brown">
            📍 {area.name} <span className="text-sm text-kirana-brown/60">· {area.tag}</span>
          </span>
          <MoveHint verb="discover" />
        </div>

        <div data-testid="mohalla-area" className="play-surface relative h-[66vh] min-h-[460px] w-full overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow">
          <MohallaDecor decor={area.decor} />

          {/* tap-to-move floor — below zones (z-20), above decor (z-10) */}
          <div data-testid="floor-tap" className="absolute inset-0 z-[15] cursor-pointer" onClick={tapToWalk} />

          {loading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-kirana-cream/70">
              <p className="font-signboard text-2xl text-kirana-brown">Bachche aa rahe hain…</p>
            </div>
          )}

          {zones.map((z) => (
            <MohallaZone
              key={z.key}
              zone={z}
              active={activeZone && activeZone.key === z.key}
              unlocked={z.kind === "game" && memories.includes(z.id)}
              playable={z.kind === "game" && !!PLAYABLE_BY_NAME[z.name]}
              onClick={() => interact(z)}
            />
          ))}

          {/* non-game object finds — unlock a memory without a game */}
          {OBJECT_FINDS.filter((f) => f.area === area.id).map((f) => (
            <ObjectFind key={f.key} find={f} found={objMem.includes(f.key)} onClick={() => setFind(f)} />
          ))}

          <Avatar pos={pos} facing={facing} moving={moving} />

        </div>

        {/* touch controls — below the scene so they never cover a card */}
        <div className={`touch-pad mx-auto mt-3 w-max grid-cols-3 gap-1.5 ${isTouch ? "grid" : "hidden"}`}>
          <span />
          <DPad onDown={() => startHold(0, -1.5)} onUp={stopHold} icon={ArrowUp} tid="dpad-up" />
          <span />
          <DPad onDown={() => startHold(-1.5, 0)} onUp={stopHold} icon={ArrowLeft} tid="dpad-left" />
          <DPad onDown={() => activeZone && interact(activeZone)} onUp={() => {}} label="⏎" tid="dpad-enter" />
          <DPad onDown={() => startHold(1.5, 0)} onUp={stopHold} icon={ArrowRight} tid="dpad-right" />
          <span />
          <DPad onDown={() => startHold(0, 1.5)} onUp={stopHold} icon={ArrowDown} tid="dpad-down" />
          <span />
        </div>
      </main>

      <GameCard
        open={!!selected}
        game={selected}
        onClose={() => setSelected(null)}
        unlocked={selected ? memories.includes(selected.id) : false}
        onAdd={handleAdd}
        onPlay={(k) => { setSelected(null); setPanel((p) => ({ ...p, open: false })); setArcadeKey(k); }}
      />

      <FindMemoryCard
        find={find}
        found={find ? objMem.includes(find.key) : false}
        onClose={() => setFind(null)}
        onAdd={handleAddFind}
      />

      <GamesCollectionPanel
        key={panel.tab}
        open={panel.open}
        initialTab={panel.tab}
        onClose={() => setPanel((p) => ({ ...p, open: false }))}
        games={games}
        memories={memories}
        onSelect={(g) => setSelected(g)}
      />

      {arcadeKey && <GameArcade open={!!arcadeKey} initialKey={arcadeKey} onClose={() => setArcadeKey(null)} />}
    </div>
  );
}

const DPad = ({ onDown, onUp, icon: Icon, label, tid }) => (
  <button
    data-testid={tid}
    onPointerDown={(e) => { e.preventDefault(); onDown(); }}
    onPointerUp={onUp}
    onPointerLeave={onUp}
    onContextMenu={(e) => e.preventDefault()}
    className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-kirana-brown bg-kirana-cream/90 text-kirana-brown hard-shadow-sm"
  >
    {Icon ? <Icon className="h-5 w-5" strokeWidth={3} /> : <span className="font-bold">{label}</span>}
  </button>
);
